import {
  Button,
  Label,
  ListPage,
  RefreshButton,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@skylabs-digital/backoffice-kit';
import { Plus, X } from 'lucide-react';
import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';

import { templatesApi } from '../api/templates.api';
import { TemplateList } from '../components/templates/TemplateList';
import { CHANNEL_OPTIONS } from '../constants/channel-colors';
import { useSelectedApp } from '../context/SelectedApp';
import { buildTemplatesQuery, pageCountFrom } from './list-query';

const PAGE_SIZE = 20;

function FilterPill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors',
        active
          ? 'border-primary bg-primary/10 text-primary'
          : 'border-border bg-card text-muted-foreground hover:border-primary/40 hover:text-foreground',
      ].join(' ')}
    >
      {children}
    </button>
  );
}

export function TemplatesPage() {
  const [channelFilter, setChannelFilter] = useState<string[]>([]);
  const [scopeFilter, setScopeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);

  const hasFilters = channelFilter.length > 0 || scopeFilter !== '' || statusFilter !== '';

  const toggleChannel = (value: string) =>
    setChannelFilter((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );

  // `isActive` is deliberately absent: ListTemplatesQuerySchema does not
  // declare it, so sending it did nothing while the control looked like it
  // worked. The active/inactive filter is applied below, on the page we have
  // (noten-g0x).
  const { appId } = useSelectedApp();
  const query = buildTemplatesQuery(
    { channel: channelFilter[0] ?? null, scope: scopeFilter },
    page,
    PAGE_SIZE,
    appId,
  );

  const { data: templates, loading, error, meta, refetch } = templatesApi.useList(query);
  const { mutate: deleteTemplate } = templatesApi.useDelete();

  const allOnPage = templates ?? [];
  // Applied client-side, and only to the current page — the endpoint has no
  // isActive filter to push it down to. Narrow scope, stated plainly rather
  // than presented as a full-set filter.
  const data = statusFilter
    ? allOnPage.filter((t) => t.isActive === (statusFilter === 'active'))
    : allOnPage;

  const total = (meta as { total?: number } | undefined)?.total;
  const pageCount = pageCountFrom(total, PAGE_SIZE, page);

  const handleDelete = useCallback(
    async (id: string) => {
      await deleteTemplate(id);
      await refetch();
    },
    [deleteTemplate, refetch],
  );

  return (
    <ListPage
      title="Templates"
      description="Manage rendered content per event type and channel."
      actions={
        <div className="flex items-center gap-2">
          {hasFilters ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setChannelFilter([]);
                setScopeFilter('');
                setStatusFilter('');
              }}
            >
              <X /> Clear filters
            </Button>
          ) : null}
          <RefreshButton onRefresh={refetch} isRefreshing={loading} />
          <Button asChild>
            <Link to="/templates/new">
              <Plus /> Create template
            </Link>
          </Button>
        </div>
      }
    >
      <div className="rounded-lg border border-border bg-card p-4">
        <div className="grid gap-4 md:grid-cols-3">
          <div className="space-y-2 md:col-span-1">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Channel</Label>
            <div className="flex flex-wrap gap-1.5">
              {CHANNEL_OPTIONS.map((c) => (
                <FilterPill
                  key={c}
                  active={channelFilter.includes(c)}
                  onClick={() => toggleChannel(c)}
                >
                  {c}
                </FilterPill>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="filter-scope">Scope</Label>
            <Select
              value={scopeFilter || 'all'}
              onValueChange={(v) => setScopeFilter(v === 'all' ? '' : v)}
            >
              <SelectTrigger id="filter-scope">
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="app">App-level</SelectItem>
                <SelectItem value="tenant">Tenant-level</SelectItem>
                <SelectItem value="system">System</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="filter-status">Status</Label>
            <Select
              value={statusFilter || 'all'}
              onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}
            >
              <SelectTrigger id="filter-status">
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <TemplateList
        templates={data}
        loading={loading}
        error={error as Error | null | undefined}
        page={page}
        pageCount={pageCount}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        onDelete={handleDelete}
      />
    </ListPage>
  );
}
